import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, FolderUp, Images, Loader2, Upload, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FolderImportRow } from "./FolderImportRow";
import { useFabricLibrary, refreshFabricLibrary } from "./useFabricLibrary";
import { normalizeFabricPart, makeFabricKey } from "./fabricUtils";
import { resizeImage, codeFromFilename, isImageFile, collectDroppedFiles, postSwatch, SWATCH_MAX_SIZE } from "./swatchUpload";

const LAST_COLLECTION_KEY = "fabricImportCollection";
const CONCURRENCY = 4;

function ProgressBar({ done, total, label, testId }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div data-testid={testId}>
      <div className="flex justify-between text-xs text-zinc-600 mb-1"><span>{label}</span><span>{done} / {total}</span></div>
      <div className="h-2 rounded-full bg-zinc-200 overflow-hidden"><div className="h-full bg-orange-500 transition-[width] duration-200" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function computeStatuses(rows, collection, library) {
  const seen = new Set();
  return rows.map((r) => {
    if (r.result) return r.result;
    if (r.error) return { kind: "error", label: r.error };
    const code = normalizeFabricPart(r.code);
    if (!code) return { kind: "error", label: "Code introuvable" };
    if (r.include && seen.has(code)) return { kind: "dup", label: "Doublon" };
    if (r.include) seen.add(code);
    return library.byKey.has(makeFabricKey(collection, code)) ? { kind: "replace", label: "Sera remplacée" } : { kind: "new", label: "Nouvelle" };
  });
}

// Bulk import of a folder of swatch photos (one collection per folder, code read from each file name).
export function FabricFolderImport({ open, onClose }) {
  const library = useFabricLibrary();
  const [collection, setCollection] = useState(() => localStorage.getItem(LAST_COLLECTION_KEY) || "");
  const [rows, setRows] = useState([]);
  const [phase, setPhase] = useState("pick");
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [dragging, setDragging] = useState(false);
  const folderRef = useRef(null);
  const filesRef = useRef(null);
  const urlsRef = useRef([]);

  useEffect(() => { folderRef.current?.setAttribute("webkitdirectory", ""); }, [open, phase]);

  const statuses = useMemo(() => computeStatuses(rows, collection, library), [rows, collection, library]);
  const importable = rows.filter((r, i) => r.include && ["new", "replace"].includes(statuses[i].kind));
  const counts = statuses.reduce((acc, s) => ({ ...acc, [s.kind]: (acc[s.kind] || 0) + 1 }), {});

  const reset = () => {
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current = [];
    setRows([]);
    setPhase("pick");
  };
  const close = () => { if (phase === "importing") return; reset(); onClose(); };

  const prepare = async (fileList) => {
    const images = fileList.filter(isImageFile).sort((a, b) => a.name.localeCompare(b.name));
    if (!images.length) { toast.error("Aucune photo trouvée (JPG, PNG, WEBP)"); return; }
    setPhase("preparing");
    setProgress({ done: 0, total: images.length });
    const out = [];
    for (const [i, file] of images.entries()) {
      let thumb = null;
      try { thumb = await resizeImage(file); } catch { thumb = null; }
      const thumbUrl = thumb ? URL.createObjectURL(thumb) : null;
      if (thumbUrl) urlsRef.current.push(thumbUrl);
      out.push({ id: `${i}-${file.name}`, name: file.name, code: codeFromFilename(file.name), thumb, thumbUrl, error: thumb ? null : "Format non supporté", include: !!thumb, result: null });
      setProgress({ done: i + 1, total: images.length });
    }
    setRows(out);
    setPhase("review");
  };

  const runImport = async () => {
    const coll = collection.trim();
    localStorage.setItem(LAST_COLLECTION_KEY, coll);
    const queue = rows.map((r, i) => ({ r, s: statuses[i] })).filter(({ r, s }) => r.include && ["new", "replace"].includes(s.kind));
    const total = queue.length;
    setPhase("importing");
    setProgress({ done: 0, total });
    const results = {};
    let done = 0;
    const worker = async () => {
      while (queue.length) {
        const { r, s } = queue.shift();
        try {
          await postSwatch(coll, r.code, r.thumb, { resize: false });
          results[r.id] = { kind: "ok", label: s.kind === "replace" ? "Remplacée" : "Ajoutée" };
        } catch (e) {
          results[r.id] = { kind: "fail", label: e.message };
        }
        done += 1;
        setProgress({ done, total });
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    await refreshFabricLibrary();
    setRows((prev) => prev.map((r) => ({ ...r, result: results[r.id] || { kind: r.error || !r.include ? "error" : "dup", label: "Non importée" } })));
    setPhase("done");
    const ok = Object.values(results).filter((x) => x.kind === "ok").length;
    const failed = total - ok;
    if (ok) toast.success(`${ok} photo(s) importée(s) dans « ${normalizeFabricPart(coll)} »`);
    if (failed) toast.error(`${failed} photo(s) en échec`);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    collectDroppedFiles(e.dataTransfer).then(prepare);
  };

  if (!open) return null;
  const locked = phase === "importing" || phase === "done";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4" onClick={close} data-testid="folder-import-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <h3 className="font-semibold text-zinc-900 font-['Manrope'] flex items-center gap-2"><FolderUp className="w-5 h-5 text-orange-500" />Importer un dossier de toiles</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Le code est lu dans le nom de chaque fichier. Photos réduites à {SWATCH_MAX_SIZE} px. Les codes existants sont remplacés.</p>
          </div>
          <button onClick={close} className="p-2 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="folder-import-close"><X className="w-5 h-5 text-zinc-500" /></button>
        </div>

        <div className="px-5 pt-4">
          <label className="text-xs font-medium text-zinc-600">Collection (pour toutes les photos du dossier)</label>
          <Input value={collection} onChange={(e) => setCollection(e.target.value)} disabled={locked} placeholder="S&P SELECTION 2025" className="h-9 mt-1" data-testid="folder-import-collection" />
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 min-h-0">
          {phase === "pick" && (
            <div onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}
              className={`rounded-xl border-2 border-dashed p-10 text-center transition-colors ${dragging ? "border-orange-400 bg-orange-50" : "border-zinc-300 bg-zinc-50"}`} data-testid="folder-import-dropzone">
              <FolderUp className="w-10 h-10 mx-auto text-zinc-400 mb-3" />
              <p className="font-medium text-zinc-700">Glissez le dossier de photos ici</p>
              <p className="text-xs text-zinc-500 mt-1 mb-4">ex. docril_collection_stripes_<b>00510</b>_essential_sesane-olive.jpg → code 00510</p>
              <div className="flex justify-center gap-2">
                <input ref={folderRef} type="file" multiple className="hidden" onChange={(e) => { const f = [...(e.target.files || [])]; e.target.value = ""; prepare(f); }} data-testid="folder-import-folder-input" />
                <input ref={filesRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => { const f = [...(e.target.files || [])]; e.target.value = ""; prepare(f); }} data-testid="folder-import-files-input" />
                <Button variant="outline" size="sm" onClick={() => folderRef.current?.click()} className="gap-1.5" data-testid="folder-import-pick-folder"><FolderUp className="w-4 h-4" />Choisir un dossier</Button>
                <Button variant="outline" size="sm" onClick={() => filesRef.current?.click()} className="gap-1.5" data-testid="folder-import-pick-files"><Images className="w-4 h-4" />Choisir des photos</Button>
              </div>
            </div>
          )}

          {phase === "preparing" && <ProgressBar done={progress.done} total={progress.total} label="Préparation et réduction des photos…" testId="folder-import-preparing" />}

          {["review", "importing", "done"].includes(phase) && (
            <>
              <div className="flex flex-wrap gap-2 mb-3 text-xs" data-testid="folder-import-summary">
                <span className="px-2 py-1 rounded-md bg-zinc-100 text-zinc-700">{rows.length} photo(s)</span>
                {counts.new > 0 && <span className="px-2 py-1 rounded-md bg-emerald-50 text-emerald-700">{counts.new} nouvelle(s)</span>}
                {counts.replace > 0 && <span className="px-2 py-1 rounded-md bg-blue-50 text-blue-700">{counts.replace} remplacée(s)</span>}
                {(counts.error || 0) + (counts.dup || 0) > 0 && <span className="px-2 py-1 rounded-md bg-amber-50 text-amber-700">{(counts.error || 0) + (counts.dup || 0)} à vérifier</span>}
                {counts.ok > 0 && <span className="px-2 py-1 rounded-md bg-emerald-100 text-emerald-800">{counts.ok} importée(s)</span>}
                {counts.fail > 0 && <span className="px-2 py-1 rounded-md bg-red-50 text-red-700">{counts.fail} échec(s)</span>}
              </div>
              {phase === "importing" && <div className="mb-3"><ProgressBar done={progress.done} total={progress.total} label="Import en cours…" testId="folder-import-progress" /></div>}
              <div className="rounded-lg border border-zinc-200">
                {rows.map((r, i) => (
                  <FolderImportRow key={r.id} idx={i} row={r} status={statuses[i]} locked={locked}
                    onChange={(patch) => setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, ...patch } : x)))} />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-zinc-200">
          <div>
            {(phase === "review" || phase === "done") && (
              <Button variant="ghost" size="sm" onClick={reset} className="gap-1.5 text-zinc-600" data-testid="folder-import-restart"><RotateCcw className="w-4 h-4" />Autre dossier</Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={close} disabled={phase === "importing"} data-testid="folder-import-cancel">{phase === "done" ? "Fermer" : "Annuler"}</Button>
            {phase !== "done" && (
              <Button size="sm" onClick={runImport} disabled={phase !== "review" || !collection.trim() || !importable.length} className="gap-1.5 bg-orange-500 hover:bg-orange-600" data-testid="folder-import-submit">
                {phase === "importing" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Importer {importable.length} photo(s)
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
